import { Outlet, NavLink, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import ThemeToggle from './ThemeToggle';
import { Home, Plus, List, CreditCard, Settings, LogOut, Menu, X, Grid, MessageCircle, Gift, Bell, Phone, Book } from 'react-feather';
import { useState, useEffect } from 'react';
import { useUnreadMessages } from '../hooks/useUnreadMessages';
import { formatCurrencyWithConversion } from '../utils/currency';
import BrandWordmark from './BrandWordmark';
import AdsDisplay from './AdsDisplay';
import FeatureNotificationModal from './FeatureNotificationModal';
import { authAPI } from '../api';
import './Layout.css';

export default function Layout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const { unreadCount } = useUnreadMessages();
  const isAdminRole = user?.role === 'admin' || user?.role === 'super_admin';
  const shouldShowAds = !location.pathname.startsWith('/dashboard/deposit');
  const [publicSettings, setPublicSettings] = useState(null);

  useEffect(() => {
    authAPI.publicSettings().then(res => {
      // axios response: res.data.settings
      setPublicSettings(res?.data?.settings || {});
    }).catch(() => {});
  }, []);

  const handleLogout = () => { logout(); navigate('/'); };

  const vnStatus = publicSettings?.virtual_numbers_status || 'beta';
  // Admins always see it; regular users see it if active or if they are testers in beta
  const showVirtualNumbers =
    isAdminRole ||
    vnStatus === 'active' ||
    (vnStatus === 'beta' && (user?.is_tester === 1 || user?.is_tester === true));

  return (
    <div className="layout">
      {/* Mobile top nav bar */}
      <div className="mobile-topnav">
        <button className="mobile-menu-toggle" onClick={() => setSidebarOpen(!sidebarOpen)}>
          {sidebarOpen ? <X size={24} /> : <Menu size={24} />}
        </button>
        <div className="mobile-logo">
          <BrandWordmark className="mobile-brand-wordmark" />
        </div>
        <div className="mobile-topnav-controls">
          <button 
            className="notification-bell-mobile" 
            onClick={() => navigate('/dashboard/support')}
            title={`Support messages (${unreadCount})`}
          >
            <Bell size={18} />
            {unreadCount > 0 && (
              <span className="notification-badge">{unreadCount > 9 ? '9+' : unreadCount}</span>
            )}
          </button>
          <ThemeToggle />
        </div>
      </div>

      {/* Desktop top nav bar */}
      <div className="desktop-topnav">
        <div className="topnav-controls">
          <button 
            className="notification-bell" 
            onClick={() => navigate('/dashboard/support')}
            title={`Support messages (${unreadCount})`}
            style={{ position: 'relative' }}
          >
            <Bell size={18} />
            {unreadCount > 0 && (
              <span className="notification-badge">{unreadCount > 9 ? '9+' : unreadCount}</span>
            )}
          </button>
          <ThemeToggle />
        </div>
      </div>

      <aside className={`sidebar ${sidebarOpen ? 'open' : ''}`}>
        <div className="sidebar-logo">
          <BrandWordmark className="logo-text" />
        </div>

        <nav className="sidebar-nav">
          <NavLink to="/dashboard" className={({isActive}) => `sidebar-link ${isActive ? 'active' : ''}`}
            onClick={() => setSidebarOpen(false)}>
            <Home size={18} /> Dashboard
          </NavLink>
          <NavLink to="/dashboard/orders/new" className={({isActive}) => `sidebar-link ${isActive ? 'active' : ''}`}
            onClick={() => setSidebarOpen(false)}>
            <Plus size={18} /> New Order
          </NavLink>
          <NavLink to="/dashboard/services" className={({isActive}) => `sidebar-link ${isActive ? 'active' : ''}`}
            onClick={() => setSidebarOpen(false)}>
            <Grid size={18} /> Services
          </NavLink>
          <NavLink to="/dashboard/orders" className={({isActive}) => `sidebar-link ${isActive ? 'active' : ''}`}
            onClick={() => setSidebarOpen(false)}>
            <List size={18} /> My Orders
          </NavLink>
          <NavLink to="/dashboard/deposit" className={({isActive}) => `sidebar-link ${isActive ? 'active' : ''}`}
            onClick={() => setSidebarOpen(false)}>
            <CreditCard size={18} /> Add Funds
          </NavLink>
          <NavLink to="/dashboard/referrals" className={({isActive}) => `sidebar-link ${isActive ? 'active' : ''}`}
            onClick={() => setSidebarOpen(false)}>
            <Gift size={18} /> Referrals
          </NavLink>
          {showVirtualNumbers && (
            <NavLink to="/dashboard/virtual-numbers" className={({isActive}) => `sidebar-link ${isActive ? 'active' : ''}`}
              onClick={() => setSidebarOpen(false)}>
              <Phone size={18} /> Virtual Numbers
            </NavLink>
          )}
          <NavLink to="/dashboard/settings" className={({isActive}) => `sidebar-link ${isActive ? 'active' : ''}`}
            onClick={() => setSidebarOpen(false)}>
            <Settings size={18} /> Settings
          </NavLink>
          <NavLink to="/dashboard/support" className={({isActive}) => `sidebar-link ${isActive ? 'active' : ''}`}
            onClick={() => setSidebarOpen(false)}>
            <MessageCircle size={18} /> Support
          </NavLink>
          <NavLink to="/dashboard/guide" className={({isActive}) => `sidebar-link ${isActive ? 'active' : ''}`}
            onClick={() => setSidebarOpen(false)}>
            <Book size={18} /> Guide
          </NavLink>
        </nav>

        {isAdminRole && (
          <NavLink to="/admin" className="sidebar-link admin-panel-link"
            onClick={() => setSidebarOpen(false)}>
            <Settings size={18} /> Admin Panel
          </NavLink>
        )}

        <div className="sidebar-user">
          <div className="user-avatar">{user?.username?.[0]?.toUpperCase()}</div>
          <div className="user-info">
            <div className="user-header">
              <div className="user-name">{user?.username}</div>
              <div className="user-balance">{formatCurrencyWithConversion(user?.balance || 0, user?.currency || 'USD')}</div>
            </div>
          </div>
        </div>

        {/* Logout button */}
        <button 
          className="sidebar-logout-btn"
          onClick={handleLogout}
          title="Logout"
        >
          <LogOut size={18} /> Logout
        </button>
      </aside>

      {/* Mobile sidebar backdrop */}
      {sidebarOpen && <div className="sidebar-backdrop" onClick={() => setSidebarOpen(false)} />}

      <main className="main-content">
        {shouldShowAds && <AdsDisplay adSlot="banner" className="banner" />}
        <Outlet />
      </main>

      <FeatureNotificationModal />
    </div>
  );
}
